import type { FileType } from "../../types/DataTypes";

export enum InvoiceServiceType {
  MSA = "Medicare Set-Aside",
  MSA_111 = "MSA-111",
  NTMSA = "Non-Threshold MSA",
  MCP = "MCP",
  LCP = "LCP",
  PRE_MSA = "Pre-MSA Snapshot",
  AMENDED_REVIEW = "Amended Review",
  AMENDED_REVIEW_SCREENING = "Amended Review Screening",
  LIEN = "LIEN",
  CON = "CON",
  AUD = "AUD",
  DOCUMENT_MANAGEMENT = "Document Management",
}

export type InvoiceLineItemType = {
  id: number;
  invoice: number;
  description: string;
  quantity: number;
  rate: number;
  total: number;
  created: string;
  assignee?: number; // user ID
  date?: string; // date in string format
  rate_type?: string | null;
};

export type InvoiceType = {
  id: number;
  organization: number;
  file: number;
  quickbooks_customer: number;
  quickbooks_workspace: number;
  quickbooks_invoice_number: string | null;
  service: number;
  terms: string;
  date_posted: string;
  due_date: string;
  line_items: InvoiceLineItemType[];
  total: number;
  client: number;
  email_subject: string;
  billing_email_address: string | null;
  description: string;
  service_type: string;
  service_status: string;
  entity_type?: string;
  entity_id?: number;
  lien_id?: number;
  liens?: number[];
  invoice_type:
    | "file_service"
    | "section_111"
    | "future_medical"
    | "lien"
    | "document_management";
  is_cms_invoice: boolean;
  status: string;
  payment_status: string;
  email_to_customer_when_finalized: boolean;
  delivery_status: string;
  manually_sent_by: number | null;
  manually_sent_at: string | null;
  invoice_has_rate_type: boolean;
  // Fee types archived on the invoice's client, so the line-item type
  // selector can hide them. Optional because older cached payloads and
  // test fixtures may not carry it; treated as empty when absent. This is
  // deliberately looser than ClientType.archived_pricing_fields (required):
  // the client detail endpoint always returns the field, but an invoice
  // payload is a snapshot that may predate it.
  client_archived_pricing_fields?: string[];
};

/**
 * Represents an entity that can be invoiced, note that client is always required
 */
export type InvoiceableEntity = {
  id: number;
  organization: number;
  client: {
    id: number;
    name: string;
    email: string;
    quickbooks_workspace?: number;
  };
  service?: number;
  file?: FileType | { id: number };
  type: string;
  label: string;
  lien_id?: number;
};

/**
 * configuration options for the InvoiceContainer
 */
export type InvoiceContainerConfig = {
  showHistory?: boolean;
  invoiceType?: "file_service" | "section_111" | "future_medical" | "lien";
  no_invoice_required?: boolean;
  onNoInvoiceRequiredChange?: (value: boolean) => void;
  showCMS?: boolean;
  filterCMSInvoices?: boolean; // when true, only show non-CMS invoices; when false, show all invoices
};

/**
 * Request payload for generating a default line item
 */
export type DefaultLineItemRequest = {
  invoice_id: number;
  pricing_field: string;
};

/**
 * Response from the default line item API
 */
export type DefaultLineItemResponse = {
  id: number;
  invoice: number;
  description: string;
  quantity: string;
  rate: string;
  total: number;
};
